import { supabase } from './supabase'
import type { MatchLike } from './bracket-math'

/**
 * Single-elimination brackets (beta) — client data access.
 *
 * Reads come straight off `tournament_brackets` / `tournament_bracket_matches`
 * (RLS: public read-only). Writes go exclusively through the SECURITY DEFINER
 * RPCs from migration 20260813000000_tournament_brackets — the tables have no
 * write policies, and the RPCs enforce the organizer-only rules server-side.
 * Structure math (advancement slots, byes, labels) lives in lib/bracket-math.
 */

export interface Bracket {
  id: string
  tournamentId: string
  /** Bracket slots (power of two). */
  size: number
  rounds: number
  status: 'active' | 'completed'
}

/** A player's identity as snapshotted into the match at generation time. */
export interface BracketPlayer {
  userId: string
  gamertag: string
  avatar: string | null
}

export interface BracketMatch {
  id: string
  round: number
  /** 0-based slot within the round. */
  position: number
  p1: BracketPlayer | null
  p2: BracketPlayer | null
  p1Score: number | null
  p2Score: number | null
  winnerUserId: string | null
  isBye: boolean
}

interface MatchRow {
  id: string
  round: number
  position: number
  p1_user_id: string | null
  p1_gamertag: string | null
  p1_avatar: string | null
  p2_user_id: string | null
  p2_gamertag: string | null
  p2_avatar: string | null
  p1_score: number | null
  p2_score: number | null
  winner_user_id: string | null
  is_bye: boolean
}

function mapPlayer(
  userId: string | null,
  gamertag: string | null,
  avatar: string | null
): BracketPlayer | null {
  return userId ? { userId, gamertag: gamertag ?? 'Unknown', avatar } : null
}

/** Bridge to the pure bracket-math helpers (progress, editability). */
export function toMatchLike(m: BracketMatch): MatchLike {
  return {
    round: m.round,
    position: m.position,
    p1UserId: m.p1?.userId ?? null,
    p2UserId: m.p2?.userId ?? null,
    winnerUserId: m.winnerUserId,
    isBye: m.isBye,
  }
}

function mapMatch(row: MatchRow): BracketMatch {
  return {
    id: row.id,
    round: row.round,
    position: row.position,
    p1: mapPlayer(row.p1_user_id, row.p1_gamertag, row.p1_avatar),
    p2: mapPlayer(row.p2_user_id, row.p2_gamertag, row.p2_avatar),
    p1Score: row.p1_score,
    p2Score: row.p2_score,
    winnerUserId: row.winner_user_id,
    isBye: row.is_bye,
  }
}

/** The tournament's bracket with all matches, or null when none exists. */
export async function getBracket(
  tournamentId: string
): Promise<{ bracket: Bracket; matches: BracketMatch[] } | null> {
  const { data: b, error } = await supabase
    .from('tournament_brackets')
    .select('id, tournament_id, size, rounds, status')
    .eq('tournament_id', tournamentId)
    .maybeSingle()

  if (error || !b) {
    if (error) console.error('getBracket error:', error.message)
    return null
  }

  const { data: rows, error: mErr } = await supabase
    .from('tournament_bracket_matches')
    .select(
      'id, round, position, p1_user_id, p1_gamertag, p1_avatar, p2_user_id, p2_gamertag, p2_avatar, p1_score, p2_score, winner_user_id, is_bye'
    )
    .eq('bracket_id', b.id)
    .order('round', { ascending: true })
    .order('position', { ascending: true })

  if (mErr) {
    console.error('getBracket matches error:', mErr.message)
    return null
  }

  return {
    bracket: {
      id: b.id as string,
      tournamentId: b.tournament_id as string,
      size: b.size as number,
      rounds: b.rounds as number,
      status: b.status as Bracket['status'],
    },
    matches: ((rows ?? []) as MatchRow[]).map(mapMatch),
  }
}

/** Whether a bracket exists — cheap check for the public detail page. */
export async function hasBracket(tournamentId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('tournament_brackets')
    .select('id')
    .eq('tournament_id', tournamentId)
    .maybeSingle()
  if (error) {
    console.error('hasBracket error:', error.message)
    return false
  }
  return !!data
}

/**
 * Generate the bracket from the confirmed field (organizer-only, server-side
 * shuffle + bye assignment).
 */
export async function generateBracket(
  tournamentId: string
): Promise<{ bracketId: string } | { error: string }> {
  const { data, error } = await supabase.rpc('generate_tournament_bracket', {
    p_tournament_id: tournamentId,
  })
  if (error || !data) {
    console.error('generateBracket error:', error?.message)
    return { error: error?.message ?? 'Could not generate the bracket.' }
  }
  return { bracketId: data as string }
}

/**
 * Record a match result: explicit winner (advances into the next round),
 * optional cosmetic scores. Blocked once the downstream match is decided.
 */
export async function setMatchResult(
  matchId: string,
  winnerUserId: string,
  p1Score?: number | null,
  p2Score?: number | null
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.rpc('set_bracket_match_result', {
    p_match_id: matchId,
    p_winner_user_id: winnerUserId,
    p_p1_score: p1Score ?? null,
    p_p2_score: p2Score ?? null,
  })
  if (error) {
    console.error('setMatchResult error:', error.message)
    return { ok: false, error: error.message }
  }
  return { ok: true }
}

/** Clear a recorded result, pulling the winner back out of the next round. */
export async function clearMatchResult(
  matchId: string
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.rpc('clear_bracket_match_result', {
    p_match_id: matchId,
  })
  if (error) {
    console.error('clearMatchResult error:', error.message)
    return { ok: false, error: error.message }
  }
  return { ok: true }
}

/** Delete the bracket and all its matches (organizer-only). */
export async function deleteBracket(
  tournamentId: string
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.rpc('delete_tournament_bracket', {
    p_tournament_id: tournamentId,
  })
  if (error) {
    console.error('deleteBracket error:', error.message)
    return { ok: false, error: error.message }
  }
  return { ok: true }
}
